import React from 'react';
import { Box, Container, useTheme, useMediaQuery, Typography, Grid, Link as MuiLink, Divider, alpha } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import Header from './Header';

// A modern, responsive layout component for consistent page structure
const Layout = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { pathname } = useLocation();
  const isMapPage = pathname === '/map' || pathname === '/agencies';

  const footerLinks = [
    { title: 'Navigation', items: [
      { name: 'Home', path: '/' },
      { name: 'About', path: '/about' },
      { name: 'Judicial Agency Map', path: '/map' },
      { name: 'Statistics', path: '/statistics' },
      { name: 'Submit Misconduct', path: '/submit-misconduct' },
    ]},
    { title: 'Resources', items: [
      { name: 'Privacy Policy', path: '/privacy' },
      { name: 'Terms of Use', path: '/terms' },
      { name: 'FAQ', path: '/faq' },
      { name: 'Contact Us', path: '/contact' },
    ]},
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        height: isMapPage ? '100vh' : undefined,
        overflow: isMapPage ? 'hidden' : undefined,
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Header />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          pb: isMapPage ? 1 : isMobile ? 3 : 5,
          px: isMapPage ? { xs: 1, md: 1.5 } : isMobile ? 2 : 3,
        }}
      >
        <Container
          maxWidth={isMapPage ? false : 'xl'}
          disableGutters={isMapPage}
          sx={
            isMapPage
              ? {
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  '& > *': {
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                  },
                }
              : undefined
          }
        >
          {children}
        </Container>
      </Box>
      {isMapPage ? null : (
      <Box
        component="footer"
        sx={{
          pt: 6,
          pb: 4,
          px: 2,
          mt: 'auto',
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
          color: theme.palette.primary.contrastText,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative element */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: `radial-gradient(circle, ${alpha('#00B8D4', 0.2)} 0%, ${alpha('#00B8D4', 0)} 70%)`,
            zIndex: 0,
          }}
        />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ mb: 3 }}>
                <Link to="/" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                    <img 
                      src="/ReckoningProject.stacked.logo.png" 
                      alt="The Reckoning Project" 
                      style={{ 
                        height: '48px',
                        width: 'auto',
                        filter: 'brightness(0) invert(1)',
                        opacity: 0.9
                      }} 
                    />
                  </Box>
                  <Typography 
                    variant="h6" 
                    component="div"
                    sx={{ 
                      fontWeight: 700,
                      letterSpacing: '-0.01em'
                    }}
                  >
                  </Typography>
                </Link>
              </Box>
              <Typography variant="body2" sx={{ mb: 3, opacity: 0.9, maxWidth: '300px' }}>
                Dedicated to transparency and accountability through data-driven analysis of misconduct.
              </Typography>
            </Grid>
            
            {footerLinks.map((section) => (
              <Grid item xs={6} md={2} key={section.title}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  {section.title}
                </Typography>
                <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                  {section.items.map((link) => (
                    <Box component="li" key={link.name} sx={{ mb: 1 }}>
                      <MuiLink
                        component={Link}
                        to={link.path}
                        sx={{ 
                          color: 'inherit', 
                          opacity: 0.8,
                          textDecoration: 'none',
                          '&:hover': { 
                            opacity: 1,
                            textDecoration: 'underline',
                          },
                          transition: 'all 0.2s',
                          fontSize: '0.9rem',
                        }}
                      >
                        {link.name}
                      </MuiLink>
                    </Box>
                  ))}
                </Box>
              </Grid>
            ))}
          </Grid>
          
          <Divider sx={{ my: 4, borderColor: alpha('#fff', 0.1) }} />
          
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'center', sm: 'center' } }}>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              © {new Date().getFullYear()} The Reckoning Project. All rights reserved.
            </Typography>
            <Box sx={{ mt: { xs: 2, sm: 0 } }}>
              <MuiLink sx={{ color: 'inherit', opacity: 0.8, mx: 1, fontSize: '0.75rem' }}>Privacy</MuiLink>
              <MuiLink sx={{ color: 'inherit', opacity: 0.8, mx: 1, fontSize: '0.75rem' }}>Terms</MuiLink>
              <MuiLink sx={{ color: 'inherit', opacity: 0.8, mx: 1, fontSize: '0.75rem' }}>Cookies</MuiLink>
            </Box>
          </Box>
        </Container>
      </Box>
      )}
    </Box>
  );
};

export default Layout;
